"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { nFormatter } from "@/lib/utils";

type BrokerLogo = { name: string; logo: string };

const featuredBrokers: BrokerLogo[] = [
  { name: "Fidelity", logo: "https://cdn.brandfetch.io/fidelity.com/w/400/h/400" },
  { name: "Robinhood", logo: "https://cdn.brandfetch.io/robinhood.com/w/400/h/400" },
  { name: "Schwab", logo: "https://cdn.brandfetch.io/schwab.com/w/400/h/400" },
  { name: "E*Trade", logo: "https://cdn.brandfetch.io/etrade.com/w/400/h/400" },
  { name: "Interactive Brokers", logo: "https://cdn.brandfetch.io/idcABCQwX-/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1667570681287" },
  { name: "tastytrade", logo: "https://cdn.brandfetch.io/tastytrade.com/w/400/h/400" },
];

const moreBrokers: BrokerLogo[] = [
  { name: "Tradier", logo: "https://cdn.brandfetch.io/tradier.com/w/400/h/400" },
  { name: "Wealthsimple", logo: "https://cdn.brandfetch.io/wealthsimple.com/w/400/h/400" },
  { name: "Webull", logo: "https://cdn.brandfetch.io/webull.com/w/400/h/400" },
  { name: "Moomoo", logo: "https://cdn.brandfetch.io/moomoo.com/w/400/h/400" },
  { name: "Alpaca (Coming Soon)", logo: "https://cdn.brandfetch.io/id3ddNjt-I/theme/light/symbol.svg?c=1bxid64Mup7aczewSAYMX&t=1668081387533" },
  { name: "Questrade (Coming Soon)", logo: "https://cdn.brandfetch.io/questrade.com/w/400/h/400" },
  { name: "TradeStation (Coming Soon)", logo: "https://cdn.brandfetch.io/tradestation.com/w/400/h/400" },
  { name: "Public (Coming Soon)", logo: "https://cdn.brandfetch.io/public.com/w/400/h/400" },
];

const FALLBACK_BALANCE = 180_000_000;
const COUNT_UP_DURATION_MS = 2000;

function useCountUp(target: number, durationMs: number) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (target <= 0) return;
    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}

export default function BrokerMarquee() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/public/stats")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const bal = data.totalBalance;
        setTotalBalance(bal && bal > 0 ? bal : FALLBACK_BALANCE);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTotalBalance(FALLBACK_BALANCE);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const animatedBalance = useCountUp(loaded ? totalBalance : 0, COUNT_UP_DURATION_MS);
  const displayBalance = loaded
    ? `$${nFormatter(animatedBalance, 0)}+`
    : "$180M+";

  const handleCtaClick = useCallback(() => {
    window.location.href = "/dashboard/broker-integration";
  }, []);

  return (
    <section className="relative py-8 sm:py-12">
      <div className="container">
        <div className="flex flex-col items-center gap-2 mb-6">
          <span className="inline-block rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-mono tracking-widest text-muted-foreground uppercase">
            Secure Broker Integrations
          </span>
          <h3 className="text-center text-2xl sm:text-3xl md:text-4xl tracking-tight text-foreground">
            <span className="tabular-nums">{displayBalance}</span>{" "}
            <span className="text-muted-foreground font-medium">in connected portfolios</span>
          </h3>
          <p className="text-center text-sm text-muted-foreground max-w-md">
            Used daily by 2,000+ options traders across 14+ US brokers.
          </p>
          <Link
            href="/security"
            className="text-center text-xs text-muted-foreground/70 max-w-md underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            Your broker connection is read-only. QuantWheel never sees your password and cannot place trades.
          </Link>
        </div>
      </div>
      
      {/* Featured brokers - static grid */}
      <div className="container mt-4">
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
          {featuredBrokers.map((b) => (
            <LogoItem key={b.name} name={b.name} logo={b.logo} featured />
          ))}
        </div>
        
        {/* Expand/Collapse button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors px-4 py-2 rounded-full border border-border/50 hover:border-primary/50 bg-muted/30"
          >
            {isExpanded ? (
              <>
                <span>Show less</span>
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                <span>and {moreBrokers.length} more...</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
        
        {/* Expanded section */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-border/30">
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
              {moreBrokers.map((b) => (
                <LogoItem key={b.name} name={b.name} logo={b.logo} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="flex flex-col items-center gap-2 mt-8">
          <button
            onClick={handleCtaClick}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-medium text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200 hover:bg-primary/90"
          >
            <span className="tracking-wide uppercase">Connect Your Portfolio</span>
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-xs text-muted-foreground/60">
            See How to Maximize Returns on Your Wheel in 60 seconds &bull; $1 for 7 days &bull; Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}

function LogoItem({ name, logo, featured }: BrokerLogo & { featured?: boolean }) {
  const [hasError, setHasError] = useState(false);
  
  const isComingSoon = name.includes("Coming Soon");
  const displayName = name.replace(" (Coming Soon)", "");
  
  return (
    <div className={`flex flex-col items-center gap-2 ${featured ? 'min-w-[80px]' : 'min-w-[70px]'}`}>
      <div className={`${featured ? 'h-12 w-12 sm:h-14 sm:w-14' : 'h-10 w-10 sm:h-12 sm:w-12'} overflow-hidden flex items-center justify-center rounded-lg bg-muted/30 p-2 ${isComingSoon ? 'opacity-60' : ''}`}>
        {hasError ? (
          <div className="h-full w-full flex items-center justify-center text-lg font-medium text-muted-foreground">
            {displayName.charAt(0)}
          </div>
        ) : (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={logo}
            alt={`${displayName} logo - broker integration for options trading`}
            className="h-full w-full object-contain"
            loading="lazy"
            decoding="async"
            onError={() => setHasError(true)}
          />
        )}
      </div>
      <span className={`text-center leading-tight ${featured ? 'text-xs sm:text-sm' : 'text-[10px] sm:text-xs'} ${isComingSoon ? 'text-muted-foreground' : 'text-foreground/80'}`}>
        {displayName}
        {isComingSoon && <span className="block text-[9px] text-muted-foreground/70">(Coming Soon)</span>}
      </span>
    </div>
  );
}
